import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import API, { setAccessTokenInMemory } from '../api/config';

const AuthContext = createContext(null);

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';

const EXEMPT_PATHS = [
  '/assessment', '/login', '/register',
  '/forgot-password', '/reset-password', '/verify-email', '/',
];

export const AuthProvider = ({ children }) => {
  const [user,    setUser]    = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const tryRestoreSession = async () => {
      try {
        // Use plain axios — NOT the API instance — so a 401 here doesn't
        // trigger the interceptor's clearAndRedirect before we handle it
        const refreshRes = await axios.post(
          `${BASE_URL}/auth/refresh-token`,
          {},
          { withCredentials: true }
        );
        const newAccess = refreshRes.data?.data?.accessToken;
        if (!newAccess) throw new Error('No token');
        setAccessTokenInMemory(newAccess);

        const meRes    = await API.get('/auth/me');
        const userData = meRes.data.data?.user || null;
        setUser(userData);

        if (userData?.role === 'CLIENT' && userData?.assessmentCompleted === false) {
          const current  = window.location.pathname;
          const isExempt = EXEMPT_PATHS.some(p => current === p || current.startsWith(p + '?'));
          if (!isExempt) window.location.href = '/assessment';
        }
      } catch {
        // No valid session — user needs to log in, nothing to do
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    tryRestoreSession();
  }, []);

  const login = (userData, tokens) => {
    // accessToken in memory only
    // refreshToken is already set as httpOnly cookie by the backend
    setAccessTokenInMemory(tokens.accessToken);
    setUser(userData);
  };

  const logout = async () => {
    try { await API.post('/auth/logout', {}); } catch { /* ok */ }
    setAccessTokenInMemory(null);
    setUser(null);
  };

  const isAdmin  = user?.role === 'SUPER_ADMIN';
  const isExpert = user?.role === 'EXPERT';
  const isClient = user?.role === 'CLIENT';
  const homePath = isAdmin ? '/admin' : isExpert ? '/expert/dashboard' : '/dashboard';

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, isAdmin, isExpert, isClient, homePath }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};